/**
 * Hair (Hair) is mounted under /Hair. Route prefix + storage keys
 * are isolated so the two flows do not share sessionStorage/localStorage entries.
 */

export const HAIR_APP_BASE = '/hair';
export const NATURALS_HAIR_APP_BASE = '/naturals/hair';
export const NYAFAZ_EP_HAIR_APP_BASE = '/nyafaz-ep/hair';

/** Default entry: Hair landing (same as `/Hair` index). */
export function resolveHairAppBase() {
  if (typeof window !== 'undefined') {
    const path = window.location?.pathname || '';
    if (path.startsWith(`${NYAFAZ_EP_HAIR_APP_BASE}/`) || path === NYAFAZ_EP_HAIR_APP_BASE) {
      return NYAFAZ_EP_HAIR_APP_BASE;
    }
    if (path.startsWith(`${NATURALS_HAIR_APP_BASE}/`) || path === NATURALS_HAIR_APP_BASE) {
      return NATURALS_HAIR_APP_BASE;
    }
    if (path.startsWith('/naturals/') && resolveHairTenantPortal() === 'naturals') {
      return NATURALS_HAIR_APP_BASE;
    }
  }
  return HAIR_APP_BASE;
}

export const HAIR_HOME_PATH = resolveHairAppBase();

export const HAIR_INSIGHT_SESSION_KEY = 'hair_insight_session_id';

const NATURALS_FRANCHISE_FLOW_KEY = 'naturals_franchise_flow_auth';

/** enterprise | naturals | nyafaz_ep — which salon Postgres receives Hair Insight sync. */
export function resolveHairTenantPortal() {
  if (typeof window === 'undefined') return 'enterprise';
  const path = window.location?.pathname || '';
  if (path.startsWith('/nyafaz-ep/')) return 'nyafaz_ep';
  if (path.includes('/naturals/')) return 'naturals';
  try {
    if (
      sessionStorage.getItem(NATURALS_FRANCHISE_FLOW_KEY)
      || localStorage.getItem(NATURALS_FRANCHISE_FLOW_KEY)
    ) {
      return 'naturals';
    }
  } catch {
    /* ignore */
  }
  const target =
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_TARGET;
  if (target && String(target).toLowerCase().includes('naturals')) return 'naturals';
  return 'enterprise';
}

export function setHairInsightSessionId(sessionId) {
  if (typeof window === 'undefined' || !sessionId) return;
  try {
    window.sessionStorage.setItem(HAIR_INSIGHT_SESSION_KEY, String(sessionId).trim());
  } catch {
    /* ignore */
  }
}

export function getHairInsightSessionId() {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(HAIR_INSIGHT_SESSION_KEY);
  } catch {
    return null;
  }
}

function normalizeInsightSessionId(value) {
  const id = value != null ? String(value).trim() : '';
  if (!id || !id.startsWith('HA-')) return null;
  return id;
}

/** Resolve Hair Insight session id from navigation state, URL, or sessionStorage. */
export function resolveInsightSessionId(location, seedDashboard) {
  const fromSeed = normalizeInsightSessionId(seedDashboard?.sessionId);
  if (fromSeed) return fromSeed;

  const fromState = normalizeInsightSessionId(location?.state?.insightSessionId);
  if (fromState) return fromState;

  const search = location?.search || '';
  if (search) {
    const params = new URLSearchParams(search);
    const fromQuery =
      normalizeInsightSessionId(params.get('insight_session')) ||
      normalizeInsightSessionId(params.get('session_id')) ||
      normalizeInsightSessionId(params.get('sessionId'));
    if (fromQuery) return fromQuery;
  }

  return normalizeInsightSessionId(getHairInsightSessionId());
}

export function insightDashboardPath(sessionId) {
  const id = normalizeInsightSessionId(sessionId);
  const base = resolveHairAppBase();
  if (!id) return `${base}/insight/dashboard`;
  const params = new URLSearchParams();
  params.set('insight_session', id);
  params.set('session_id', id);
  return `${base}/insight/dashboard?${params.toString()}`;
}

/** Turn an app path like /user-info into /hair/user-info. `/` → hair home (landing). */
export function hairAppPath(path) {
  const base = resolveHairAppBase();
  const raw = path == null || path === '' ? '/' : String(path);
  const p = raw.startsWith('/') ? raw : `/${raw}`;
  if (p === '/') return base;
  return `${base}${p}`;
}

export const HAIR_APP_SESSION_TOKEN_KEY = 'hair_app_session_token';
export const HAIR_SESSION_HOME_PATH_KEY = 'hair_session_home_path';
export const HAIR_DETECTION_SESSION_KEY = 'hair_detection_session_id';
export const HAIR_PENDING_SAVE_KEY = 'hair_pending_save_analysis_v1';
export const HAIR_PAGE_SETUP_TOKEN_KEY = 'hair_page_setup_token';
export const HAIR_PAGE_SETUP_DATA_PREFIX = 'hair_page_setup_data_';
export const HAIR_FLOW_STATE_PREFIX = 'hair_flow_state_';
export const HAIR_CACHE_KEY_PREFIX = 'hair_analysis_cache_';
export const HAIR_PENDING_SAVES_KEY = 'hair_analysis_pending_saves';

/** localStorage key prefix for last saved customer profile on hair user-info (per app session token). */
export const HAIR_LAST_CUSTOMER_PREFIX = 'hair_last_customer_v1_';

/** Session flag: user agreed to hair capture disclaimer for this browser tab. */
export const HAIR_DISCLAIMER_KEY = 'nyafaz_hair_disclaimer_accepted_v1';

export function hairLastCustomerStorageKey(token) {
  const t = token && String(token).trim() ? String(token).trim() : '_default';
  return `${HAIR_LAST_CUSTOMER_PREFIX}${t}`;
}
